import { CloseCircleFilled } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import { AppTable, AppTableToolbar } from "components/antd-ui";
import { StatusCell, TableLayout, TableLayoutFooter, TableTotals } from "components/table";
import { useClientPaging } from "hooks/useClientPaging";
import { memo, useMemo, useState, type ReactNode } from "react";
import { RevenueReconRow, revenueReconStateTrans } from "types/revenue";
import { formatVND } from "utils";
import dayjs from "utils/dayjs";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";

const formatMoney = (num: number) => `${formatVND(Math.round(num || 0))}đ`;
const displayDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");

export type ReconTotals = { expected: number; actual: number; difference: number };

type ReconTableProps = {
  rows: RevenueReconRow[];
  totals: ReconTotals;
  loading: boolean;
  initialTab?: string;
  leftActions?: ReactNode;
};

const columns: TableColumnsType<RevenueReconRow> = [
  {
    title: "Hợp đồng",
    key: "order",
    render: (_, row) => (
      <div>
        <div className="font-semibold">{row.order.contractCode || "--"}</div>
        <div className="text-xs text-gray-500">{row.order.opportunityName || "--"}</div>
      </div>
    ),
  },
  { title: "Khách hàng", key: "customer", render: (_, row) => row.customer?.name || "--" },
  {
    title: "Đợt thu",
    key: "content",
    render: (_, row) => (row.content ? `${row.content}${row.debtCount ? ` / ${row.debtCount} đợt` : ""}` : "--"),
  },
  { title: "Hạn thu", dataIndex: "dueDate", key: "dueDate", render: (value) => displayDate(value) },
  {
    title: "Ngày thu",
    dataIndex: "collectedDate",
    key: "collectedDate",
    render: (value, row) => (row.status === "PAID" ? displayDate(value) : "--"),
  },
  {
    title: "Dự kiến",
    dataIndex: "expectedAmount",
    key: "expectedAmount",
    align: "right",
    render: (value) => (value ? formatMoney(value) : <span className="text-gray-300">--</span>),
  },
  {
    title: "Thực tế",
    dataIndex: "actualAmount",
    key: "actualAmount",
    align: "right",
    render: (value) => (value ? formatMoney(value) : <span className="text-gray-300">--</span>),
  },
  {
    title: "Chênh lệch",
    dataIndex: "difference",
    key: "difference",
    align: "right",
    render: (value) =>
      value === 0 ? (
        <span className="text-gray-400">0đ</span>
      ) : (
        <span className={`font-semibold ${value < 0 ? "text-red-600" : "text-green-600"}`}>
          {value > 0 ? "+" : ""}
          {formatMoney(value)}
        </span>
      ),
  },
  {
    title: "Tình trạng",
    dataIndex: "state",
    key: "state",
    render: (value) => <StatusCell value={value} trans={revenueReconStateTrans} />,
  },
];

// Bảng đối chiếu công nợ dự kiến / thực tế / chênh lệch — dùng cho cửa sổ Tổng quan và danh sách theo tháng
export const ReconTable = memo(({ rows, totals, loading, initialTab = "ALL", leftActions }: ReconTableProps) => {
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState("");

  const counts = useMemo(
    () => ({
      ALL: rows.length,
      SHORT: rows.filter((row) => row.difference < 0).length,
      MATCH: rows.filter((row) => row.difference === 0).length,
      EXTRA: rows.filter((row) => row.difference > 0).length,
    }),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (tab === "SHORT" && row.difference >= 0) return false;
      if (tab === "MATCH" && row.difference !== 0) return false;
      if (tab === "EXTRA" && row.difference <= 0) return false;
      if (!keyword) return true;
      return [row.order.contractCode, row.order.opportunityName, row.customer?.name, row.content]
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });
  }, [rows, tab, search]);

  const paging = useClientPaging(visibleRows);

  return (
    <>
      <StatusTabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: "ALL", label: "Tất cả", count: counts.ALL, tone: "blue" },
          { key: "SHORT", label: "Thiếu — không thu được", count: counts.SHORT, tone: "red" },
          { key: "MATCH", label: "Khớp kế hoạch", count: counts.MATCH, tone: "green" },
          { key: "EXTRA", label: "Thu ngoài kế hoạch", count: counts.EXTRA, tone: "orange" },
        ]}
      />
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 360,
              placeholder: "Tìm theo mã hợp đồng, tên, khách hàng, đợt thu",
              onChange: setSearch,
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={() => setSearch("")} /> } },
            }}
            leftActions={leftActions}
            filterLabel={null}
          />
        }
        tabs={
          <TableTotals
            items={[
              { label: "Tổng dự kiến", value: formatMoney(totals.expected) },
              { label: "Tổng thực tế", value: formatMoney(totals.actual), tone: "primary" },
              {
                label: "Chênh lệch",
                value: `${totals.difference > 0 ? "+" : ""}${formatMoney(totals.difference)}`,
                tone: totals.difference < 0 ? "negative" : "positive",
              },
            ]}
            note="Chênh lệch từng dòng = Thực tế − Dự kiến. Dòng đỏ: công nợ đến hạn nhưng chưa thu được trong kỳ."
          />
        }
        table={
          <AppTable<RevenueReconRow>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={paging.pageRows}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />
    </>
  );
});
ReconTable.displayName = "ReconTable";
